/* eslint-disable react-native/no-inline-styles */
import React, { memo, useCallback, useMemo } from 'react'

import i18n from '@batical/i18n'
import { useContextValue } from '@batical/reducer/src'
import { CalculCodeStateKeys } from '@batical/types/src'
import { ToggleButton, ToggleButtonGroup } from '@mui/material'

import { batikalColor } from '../Colors'
import { CalculText, ViewColumnLine } from './LineStyleComponent'

export type SegmentLineProps = {
  calculCode: CalculCodeStateKeys
  segments: Array<string>
  variableName: string
  onChange?: (index: number) => void
  withoutTitle?: boolean
  first?: boolean
  last?: boolean
}

export type SegmentLineManualProps = {
  title?: string
  segments: Array<string>
  value: number
  onChange: (index: number) => void
  first?: boolean
  last?: boolean
}

const SegmentLine = (props: SegmentLineProps) => {
  const [value, update] = useContextValue(props.variableName, props.calculCode)

  const text = useMemo(() => {
    if (props.withoutTitle) {
      return undefined
    }
    return i18n.t(`${props.calculCode}_${props.variableName}`)
  }, [props.calculCode, props.variableName, props.withoutTitle])

  const onValueChange = useCallback(
    (index: number) => {
      if (props.onChange) {
        props.onChange(index)
      } else {
        update(index)
      }
    },
    [props, update]
  )

  return <SegmentLineManual title={text} value={value} onChange={onValueChange} {...props} />
}

export const SegmentLineManual = ({ title, segments, value, onChange, first = false, last = false }: SegmentLineManualProps) => {
  const handleChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, newValue: string | null) => {
      if (newValue !== null) {
        onChange(parseInt(newValue, 10))
      }
    },
    [onChange]
  )

  const showTitle = title && title !== ''

  return (
    <ViewColumnLine first={first} last={last} style={{ gap: 8, paddingTop: 10, paddingBottom: 10 }}>
      {showTitle && <CalculText style={{ paddingBottom: 4 }}>{title}</CalculText>}
      <ToggleButtonGroup
        value={String(value)}
        exclusive
        onChange={handleChange}
        fullWidth
        sx={{
          borderRadius: '10px',
          overflow: 'hidden',
          border: '1px solid #e2e8f0',
          '& .MuiToggleButton-root': {
            border: 'none',
            borderRadius: '0 !important',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 13,
            py: 1,
            color: '#64748b',
            backgroundColor: '#f8fafc',
            transition: 'all 0.2s ease',
            '&:not(:last-of-type)': {
              borderRight: '1px solid #e2e8f0',
            },
            '&.Mui-selected': {
              backgroundColor: batikalColor,
              color: '#ffffff',
              boxShadow: `0 2px 8px ${batikalColor}40`,
              '&:hover': {
                backgroundColor: batikalColor,
              }
            },
            '&:hover:not(.Mui-selected)': {
              backgroundColor: '#f1f5f9',
            }
          }
        }}>
        {segments.map((segment, i) => (
          <ToggleButton value={String(i)} key={i}>
            {segment}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </ViewColumnLine>
  )
}

export default memo(SegmentLine)
